import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'

interface ReceiptPreviewProps {
  open: boolean
  downloadUrl: string
  contentType: string
}

const createObjectUrlFromDownload = async (
  downloadUrl: string,
  contentType: string
): Promise<string> => {
  const response = await fetch(downloadUrl)
  const blob = await response.blob()
  const typedBlob = new Blob([blob], { type: contentType })
  return URL.createObjectURL(typedBlob)
}

const PreviewContainer = styled.div`
  margin-bottom: 1.5rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 8px;
  overflow: hidden;
  background-color: ${(props) => props.theme.palette.grey.grey100};

  img {
    width: 100%;
    height: auto;
    max-height: 300px;
    object-fit: contain;
    display: block;
  }

  iframe {
    width: 100%;
    height: 300px;
    border: none;
    display: block;
  }

  object {
    display: block;
  }
`

const PreviewFallback = styled.div`
  margin-bottom: 1.5rem;
  padding: 2rem;
  border: 1px dashed ${(props) => props.theme.palette.grey.grey300};
  border-radius: 8px;
  text-align: center;
  background-color: ${(props) => props.theme.palette.grey.grey100};

  a {
    color: ${(props) => props.theme.palette.primary.pink500};
    text-decoration: underline;
    cursor: pointer;
  }
`

const ReceiptPreview = React.memo(
  ({ open, downloadUrl, contentType }: ReceiptPreviewProps) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)

    useEffect(() => {
      if (!open || !downloadUrl) {
        setBlobUrl(null)
        setError(false)
        setLoading(false)
        return
      }

      let url: string | null = null
      let isActive = true
      setLoading(true)
      setError(false)

      const fetchPreview = async () => {
        try {
          const objectUrl = await createObjectUrlFromDownload(
            downloadUrl,
            contentType
          )
          if (!isActive) return
          url = objectUrl
          setBlobUrl(objectUrl)
        } catch (err) {
          if (!isActive) return
          console.error('Failed to fetch preview:', err)
          setError(true)
        } finally {
          if (!isActive) return
          setLoading(false)
        }
      }

      fetchPreview()

      return () => {
        isActive = false
        if (url) {
          URL.revokeObjectURL(url)
        }
      }
    }, [open, downloadUrl, contentType])

    if (loading) {
      return (
        <PreviewFallback>
          <Text variant='body1' color='text.secondary'>
            Loading preview...
          </Text>
        </PreviewFallback>
      )
    }

    if (error && downloadUrl) {
      return (
        <PreviewFallback>
          <Text variant='body1' color='text.secondary'>
            Preview unavailable.{' '}
            <a href={downloadUrl} target='_blank' rel='noopener noreferrer'>
              Open receipt in new tab
            </a>
          </Text>
        </PreviewFallback>
      )
    }

    if (!blobUrl) return null

    if (contentType.startsWith('image/')) {
      return (
        <PreviewContainer>
          <img src={blobUrl} alt='Receipt preview' />
        </PreviewContainer>
      )
    }

    if (contentType === 'application/pdf') {
      return (
        <PreviewContainer>
          <object
            data={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
            type='application/pdf'
            width='100%'
            height='300'
          />
        </PreviewContainer>
      )
    }

    return null
  }
)

if (process.env.NODE_ENV !== 'production') {
  ReceiptPreview.displayName = 'ReceiptPreview'
}

export default ReceiptPreview
